import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { ApiError } from '../api';

export interface AsyncState<T> {
  data: T | undefined;
  error: unknown;
  loading: boolean;
  reload: () => Promise<void>;
}

/**
 * Runs a loader on mount and whenever `deps` change. The loader is read
 * through a ref so a caller passing an inline function does not re-trigger it
 * on every render — the fetch loop the Ops UI once had.
 */
export function useAsync<T>(loader: () => Promise<T>, deps: unknown[]): AsyncState<T> {
  const loaderRef = useRef(loader);
  loaderRef.current = loader;
  const [data, setData] = useState<T>();
  const [error, setError] = useState<unknown>(null);
  const [loading, setLoading] = useState(true);

  const reload = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setData(await loaderRef.current());
    } catch (nextError) {
      setError(nextError);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);

  return { data, error, loading, reload };
}

export function PageHeader({
  eyebrow,
  title,
  description,
  actions,
}: {
  eyebrow?: string;
  title: string;
  description?: ReactNode;
  actions?: ReactNode;
}) {
  return (
    <header className="page-header">
      <div>
        {eyebrow && <p className="eyebrow">{eyebrow}</p>}
        <h1>{title}</h1>
        {description && <p className="lede">{description}</p>}
      </div>
      {actions && <div className="page-actions">{actions}</div>}
    </header>
  );
}

export function Card({ title, children, className = '', aside }: { title?: ReactNode; children: ReactNode; className?: string; aside?: ReactNode }) {
  return (
    <section className={`card ${className}`}>
      {(title || aside) && (
        <div className="card-head">
          {title && <h2>{title}</h2>}
          {aside}
        </div>
      )}
      {children}
    </section>
  );
}

export function ErrorNotice({ error, onRetry }: { error: unknown; onRetry?: () => void }) {
  if (!error) return null;
  const apiError = error instanceof ApiError ? error : null;
  return (
    <div className="notice danger" role="alert">
      <div>
        <strong>{apiError ? friendlyTitle(apiError) : 'Something went wrong'}</strong>
        <p>{apiError?.message ?? String((error as { message?: string })?.message ?? error)}</p>
        {apiError?.requestId && (
          <p className="fine">
            Reference <code>{apiError.requestId}</code> — quote it if you contact support.
          </p>
        )}
      </div>
      {onRetry && (
        <button type="button" onClick={onRetry}>
          Try again
        </button>
      )}
    </div>
  );
}

function friendlyTitle(error: ApiError): string {
  switch (error.code) {
    case 'rate_limited':
      return 'Too many requests — wait a moment';
    case 'billing_provider_disabled':
      return 'Online payments are unavailable';
    case 'payment_provider_error':
      return 'The payment provider did not respond';
    case 'validation_failed':
      return 'Please check what you entered';
    case 'conflict':
      return 'That is not possible right now';
    case 'not_found':
      return 'Not found';
    default:
      return 'Something went wrong';
  }
}

export function Notice({ tone = 'info', children }: { tone?: 'info' | 'success' | 'warning' | 'danger'; children: ReactNode }) {
  return <div className={`notice ${tone}`}>{children}</div>;
}

const STATUS_TONES: Record<string, { label: string; tone: string }> = {
  none: { label: 'Free', tone: 'neutral' },
  active: { label: 'Active', tone: 'success' },
  trialing: { label: 'Trial', tone: 'accent' },
  past_due: { label: 'Payment overdue', tone: 'warning' },
  grace_period: { label: 'Grace period', tone: 'warning' },
  cancelled: { label: 'Ends at period end', tone: 'warning' },
  expired: { label: 'Expired', tone: 'neutral' },
  suspended: { label: 'Suspended', tone: 'danger' },
  pending: { label: 'Pending', tone: 'warning' },
  completed: { label: 'Paid', tone: 'success' },
  failed: { label: 'Failed', tone: 'danger' },
  refunded: { label: 'Refunded', tone: 'neutral' },
};

export function StatusBadge({ status }: { status: string }) {
  const known = STATUS_TONES[status] ?? { label: status.replace(/_/g, ' '), tone: 'neutral' };
  return <span className={`badge ${known.tone}`}>{known.label}</span>;
}

export function PaymentBadge({ status }: { status: string }) {
  if (status === 'cancelled') return <span className="badge neutral">Cancelled</span>;
  return <StatusBadge status={status} />;
}

export function Spinner({ label }: { label?: string }) {
  return (
    <span className="spinner-wrap" role="status">
      <span className="spinner" aria-hidden="true" />
      {label && <span>{label}</span>}
    </span>
  );
}

export function Loading({ label = 'Loading…' }: { label?: string }) {
  return (
    <div className="loading">
      <Spinner label={label} />
    </div>
  );
}

/**
 * A modal confirmation built on <dialog>, so focus trapping and Escape come
 * from the browser rather than from us.
 */
export function ConfirmDialog({
  open,
  title,
  children,
  confirmLabel,
  tone = 'primary',
  busy = false,
  confirmDisabled = false,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  children: ReactNode;
  confirmLabel: string;
  tone?: 'primary' | 'danger';
  busy?: boolean;
  confirmDisabled?: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const ref = useRef<HTMLDialogElement>(null);
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;
    if (open && !dialog.open) dialog.showModal();
    if (!open && dialog.open) dialog.close();
  }, [open]);
  return (
    <dialog
      ref={ref}
      onCancel={(event) => {
        event.preventDefault();
        if (!busy) onCancel();
      }}
    >
      <h2>{title}</h2>
      <div className="dialog-body">{children}</div>
      <div className="dialog-actions">
        <button type="button" onClick={onCancel} disabled={busy}>
          Cancel
        </button>
        <button type="button" className={tone} onClick={onConfirm} disabled={busy || confirmDisabled}>
          {busy ? <Spinner /> : null}
          {confirmLabel}
        </button>
      </div>
    </dialog>
  );
}
